import { useCallback, useEffect, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  Icon,
  Modal,
  ModalContent,
  ModalContentBody,
  ModalContentHeader,
  Tabs,
  Text,
} from "@zapier/design-system";
import {
  Account,
  canvasAtom,
  fetchPermissionsAtomFamily,
  fetchSelectedMembersAtomFamily,
  fetchSelectedTeamsAtomFamily,
  isFetchingPermissionsAtom,
  isFetchingSelectedMembersAtom,
  isFetchingSelectedTeamsAtom,
  isViewOnlyModeAtom,
  Member,
  membersAtomFamily,
  orgPermissionAtomFamily,
  permissionsAtom,
  projectAsyncAtom,
  SelectablePermission,
  selectedMembersAtom,
  selectedTeamsAtom,
  teamsAtomFamily,
} from "@zapier/canvas-state";
import { useCurrentAccountId } from "@zapier/identity";
import {
  getOrgPermission,
  MemberWithPermission,
  Permission,
  Permissions,
  SelectableType,
  SHARING_MODAL_MAX_WIDTH,
  Team,
  TeamWithPermission,
} from "@zapier/permissions";
import { ACCESS_LABELS } from "@zapier/canvas-constants";

import { useEmitUserInteractionEvents } from "../../../hooks/useEmitUserInteractionEvents";
import { PublicLink } from "../../PublicLink";

import { usePrepareDownloadImage } from "./usePrepareDownloadImage";
import styles from "./PermissionsModal.module.css";

// Import styles from permissions package
import "@zapier/permissions/dist/zapier-permissions.cjs.css";

type Props = {
  onClosed: () => void;
  account: Account;
  owner: Member | undefined;
  hasMultipleTeams: boolean;
};

export const DOWNLOAD_PNG = "Download PNG";

type PayloadType = SelectableType | "account";

function createPayload(
  type: PayloadType,
  permission: SelectablePermission,
  id?: number,
) {
  const mapByType = {
    individual: "shared_users",
    team: "shared_teams",
    account: "shared_account",
  };

  if (type === "account") {
    return JSON.stringify({
      [mapByType[type]]: { relationship_type: permission },
    });
  }

  return JSON.stringify({
    [mapByType[type]]: [
      {
        [type === "individual"
          ? "recipient_customuser_id"
          : "recipient_team_id"]: id,
        relationship_type: permission,
      },
    ],
  });
}

export function PermissionsModal({
  owner,
  account,
  onClosed,
  hasMultipleTeams,
}: Props) {
  const canvas = useAtomValue(canvasAtom);
  const currentAccountId = useCurrentAccountId();
  const { handleActualDownload, isBusy } = usePrepareDownloadImage();
  const {
    emitEventOnShareCanvas,
    emitEventOnShareCanvasWithTeam,
    emitEventOnShareCanvasWithUser,
    emitEventOnShareUpsell,
  } = useEmitUserInteractionEvents();
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);

  const header = canvas.name ? `Share "${canvas.name}"` : "Share Canvas";

  const allowTeams = account?.plan_info.allow_teams;

  const [permissions, setPermissions] = useAtom(permissionsAtom);

  const isFetchingSelectedMembers = useAtomValue(isFetchingSelectedMembersAtom);
  const isFetchingSelectedTeams = useAtomValue(isFetchingSelectedTeamsAtom);
  const isFetchingPermissions = useAtomValue(isFetchingPermissionsAtom);

  const project = useAtomValue(projectAsyncAtom);

  const fetchPermissions = useSetAtom(
    fetchPermissionsAtomFamily({ canvasId: canvas.id, allowTeams }),
  );

  const fetchSelectedTeams = useSetAtom(
    fetchSelectedTeamsAtomFamily({
      currentAccountId,
      allowTeams,
      hasMultipleTeams,
    }),
  );

  const fetchSelectedMembers = useSetAtom(
    fetchSelectedMembersAtomFamily({
      currentAccountId,
      allowTeams,
    }),
  );

  const orgPermission = useAtomValue(
    orgPermissionAtomFamily({ currentAccountId, allowTeams }),
  );

  const orgPermissionForCanvas =
    getOrgPermission(permissions, "canvas", currentAccountId) ?? "no access";

  const [selectedMembers, setSelectedMembers] = useAtom(selectedMembersAtom);
  const [selectedTeams, setSelectedTeams] = useAtom(selectedTeamsAtom);

  const [search, setSearch] = useState<string>("");
  const params = { currentAccountId, search, allowTeams };
  const membersRes = useAtomValue(membersAtomFamily(params));
  const teamsRes = useAtomValue(
    teamsAtomFamily({ ...params, hasMultipleTeams }),
  );

  let membersSearchResults: Member[] = [];

  if (membersRes.state === "hasData") {
    membersSearchResults = membersRes.data || [];
  }

  let teamsSearchResults: Team[] = [];

  if (teamsRes.state === "hasData") {
    teamsSearchResults = teamsRes.data || [];
  }

  const isFetchingSearchResults =
    membersRes.state === "loading" || teamsRes.state === "loading";

  const addMemberOrTeam = useCallback(
    async (memberOrTeam: MemberWithPermission | TeamWithPermission) => {
      const isPerson = "full_name" in memberOrTeam;
      const type = isPerson ? "individual" : "team";
      const id = memberOrTeam.id;

      try {
        const payload = createPayload(type, memberOrTeam.permission, id);

        const response = await fetch(
          `/api/canvas/v1/permissions/${canvas.id}/`,
          {
            method: "PUT",
            body: payload,
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          if (isPerson) {
            setSelectedMembers((members) => [...members, memberOrTeam]);
          } else {
            setSelectedTeams((teams) => [...teams, memberOrTeam]);
          }

          isPerson
            ? emitEventOnShareCanvasWithUser(memberOrTeam.permission)
            : emitEventOnShareCanvasWithTeam(memberOrTeam.permission);
          return { success: true };
        }

        return { success: false };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";

        console.error(
          `Error adding permission for ${
            isPerson ? "member" : "team"
          } id ${id}: ${errorMessage}`,
        );

        return { success: false };
      }
    },
    [
      canvas.id,
      emitEventOnShareCanvasWithTeam,
      emitEventOnShareCanvasWithUser,
      setSelectedTeams,
      setSelectedMembers,
    ],
  );

  const changeMemberOrTeamPermission = useCallback(
    async (memberOrTeam: MemberWithPermission | TeamWithPermission) => {
      const isPerson = "full_name" in memberOrTeam;
      const type = isPerson ? "individual" : "team";
      const id = memberOrTeam.id;

      try {
        const payload = createPayload(type, memberOrTeam.permission, id);

        const response = await fetch(
          `/api/canvas/v1/permissions/${canvas.id}/`,
          {
            method: "PUT",
            body: payload,
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          if (isPerson) {
            setSelectedMembers((members) =>
              members.map((member) => {
                if (member.id === id) {
                  return memberOrTeam;
                }
                return member;
              }),
            );
          } else {
            setSelectedTeams((teams) =>
              teams.map((team) => {
                if (team.id === id) {
                  return memberOrTeam;
                }
                return team;
              }),
            );
          }

          isPerson
            ? emitEventOnShareCanvasWithUser(memberOrTeam.permission)
            : emitEventOnShareCanvasWithTeam(memberOrTeam.permission);
          return { success: true };
        }

        return { success: false };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";

        console.error(
          `Error updating permission for ${isPerson ? "person" : "team"} id ${
            memberOrTeam.id
          }: ${errorMessage}`,
        );

        return { success: false };
      }
    },
    [
      canvas.id,
      emitEventOnShareCanvasWithTeam,
      emitEventOnShareCanvasWithUser,
      setSelectedTeams,
      setSelectedMembers,
    ],
  );

  const removePermission = useCallback(
    async (memberOrTeam: MemberWithPermission | TeamWithPermission) => {
      const isPerson = "full_name" in memberOrTeam;
      const type = isPerson ? "individual" : "team";
      const id = memberOrTeam.id;

      try {
        const payload = createPayload(type, memberOrTeam.permission, id);

        const response = await fetch(
          `/api/canvas/v1/permissions/${canvas.id}/`,
          {
            method: "DELETE",
            body: payload,
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          if (isPerson) {
            setSelectedMembers((members) =>
              members.filter((member) => member.id !== id),
            );
          } else {
            setSelectedTeams((teams: TeamWithPermission[]) =>
              teams.filter((team) => team.id !== id),
            );
          }
        }

        return { success: response.ok };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";

        console.error(
          `Error removing permission for ${
            isPerson ? "member" : "team"
          } id ${id}: ${errorMessage}`,
        );
        return { success: false };
      }
    },
    [canvas.id, setSelectedTeams, setSelectedMembers],
  );

  const removeOrgPermission = useCallback(async () => {
    const permission = "no access";

    if (orgPermissionForCanvas === permission) {
      return { success: true };
    }

    const payload = createPayload("account", orgPermissionForCanvas);

    try {
      const response = await fetch(`/api/canvas/v1/permissions/${canvas.id}/`, {
        method: "DELETE",
        body: payload,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setPermissions((prevPermissions) =>
          prevPermissions.filter(
            (p) =>
              !(
                p.object.includes("canvas") &&
                p.user === `account:${currentAccountId}#member`
              ),
          ),
        );

        emitEventOnShareCanvas(ACCESS_LABELS[permission]);
      }

      return { success: response.ok };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";

      console.error(
        `Error removing permission for account id ${currentAccountId}: ${errorMessage}`,
      );
      return { success: false };
    }
  }, [
    orgPermissionForCanvas,
    canvas.id,
    currentAccountId,
    emitEventOnShareCanvas,
    setPermissions,
  ]);

  const onChangeOrgPermission = useCallback(
    async (permission: Permission) => {
      if (permission === "no access") {
        return removeOrgPermission();
      }

      try {
        const payload = createPayload("account", permission);

        const response = await fetch(
          `/api/canvas/v1/permissions/${canvas.id}/`,
          {
            method: "PUT",
            body: payload,
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          setPermissions((prevPermissions) => {
            const existingPermissionIndex = prevPermissions.findIndex(
              (p) =>
                p.object.includes("canvas") &&
                p.user === `account:${currentAccountId}#member`,
            );

            return existingPermissionIndex >= 0
              ? prevPermissions.map((p, index) =>
                  index === existingPermissionIndex
                    ? { ...p, relation: permission }
                    : p,
                )
              : [
                  {
                    object: `canvas:${canvas.id}`,
                    relation: permission,
                    user: `account:${currentAccountId}#member`,
                  },
                  ...prevPermissions,
                ];
          });

          emitEventOnShareCanvas(ACCESS_LABELS[permission]);
        }

        return { success: response.ok };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";

        console.error(`Error changing account permission: ${errorMessage}`);
        return { success: false };
      }
    },
    [
      emitEventOnShareCanvas,
      canvas.id,
      removeOrgPermission,
      setPermissions,
      currentAccountId,
    ],
  );

  useEffect(() => {
    fetchPermissions()
      .then(() => {
        Promise.allSettled([fetchSelectedTeams(), fetchSelectedMembers()]).then(
          (results) => {
            const [teamsResult, membersResult] = results;

            if (teamsResult.status !== "fulfilled") {
              console.error("Error fetching teams:", teamsResult.reason);
            }

            if (membersResult.status !== "fulfilled") {
              console.error("Error fetching members:", membersResult.reason);
            }
          },
        );
      })
      .catch((error) => {
        console.error("Error fetching permissions:", error);
      });
  }, [fetchPermissions, fetchSelectedMembers, fetchSelectedTeams]);

  const [selectedTabHref, setSelectedTabHref] = useState("#share");

  const isShareTabSelected = selectedTabHref === "#share";

  const tabs = [
    {
      href: "#share",
      text: "Share",
    },
    {
      href: "#public-link",
      text: "Public link",
    },
  ];

  const tabContentMap = {
    "#share": (
      <Permissions
        account={account}
        assetType="canvas"
        orgPermission={orgPermission}
        addMemberOrTeam={addMemberOrTeam}
        changeMemberOrTeamPermission={changeMemberOrTeamPermission}
        emitEventOnShareUpsell={emitEventOnShareUpsell}
        hasEditorAndViewerRoles={true}
        hasMultipleTeams={hasMultipleTeams}
        isViewOnlyMode={isViewOnlyMode}
        selectedMembers={selectedMembers}
        selectedTeams={selectedTeams}
        onChangeOrgPermission={onChangeOrgPermission}
        owner={owner}
        removePermission={removePermission}
        isFetchingOrgPermission={isFetchingPermissions}
        isFetchingSearchResults={isFetchingSearchResults}
        isFetchingSelectedTeamsAndMembers={
          isFetchingPermissions ||
          isFetchingSelectedMembers ||
          isFetchingSelectedTeams
        }
        onSearch={setSearch}
        project={project}
        membersSearchResults={membersSearchResults}
        teamsSearchResults={teamsSearchResults}
      />
    ),
    "#public-link": <PublicLink isViewOnlyMode={isViewOnlyMode} />,
  };

  return (
    <Modal onClosed={onClosed} aria-label={header}>
      <ModalContent maxWidth={`${SHARING_MODAL_MAX_WIDTH}px`}>
        <ModalContentHeader>
          <div className={styles.modalHeader}>
            <Text type="SectionHeader" margin="0 0 23px 0">
              {header}
            </Text>
          </div>
        </ModalContentHeader>
        <ModalContentBody scrollable={isShareTabSelected}>
          <div className={styles.tabsWrapper}>
            <Tabs
              items={tabs}
              onChooseItem={(item) => setSelectedTabHref(item.href)}
              selectedItemHref={selectedTabHref}
              size="medium"
            />
          </div>
          {tabContentMap[selectedTabHref as "#share" | "#public-link"]}
        </ModalContentBody>
        {isShareTabSelected && (
          <div className={styles.footer}>
            <button
              onClick={handleActualDownload}
              disabled={isBusy}
              className={styles.downloadButton}
            >
              <Icon
                name="actionDownload"
                color="UiPrimaryStrongest"
                size={30}
              />
              <Text
                color="UiPrimaryStrongest"
                // @ts-expect-error this is an ok fontWeight
                fontWeight={500}
                size={16}
                type="Body1"
              >
                {isBusy ? (
                  <div className={styles.busyText}>Processing</div>
                ) : (
                  DOWNLOAD_PNG
                )}
              </Text>
            </button>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
